import { useState, useEffect, useCallback } from 'react';
import { fetchSuppliers, searchSuppliers } from '../api/suppliersApi';
import type { SupplierListItem } from '../types';

interface UseSupplierOptions {
  businessId: string | null;
  autoFetch?: boolean;
}

interface UseSuppliersReturn {
  suppliers: SupplierListItem[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  search: (term: string) => Promise<void>;
  clearSearch: () => void;
  isSearching: boolean;
}

/**
 * Hook to fetch and search suppliers for a business
 */
export function useSuppliers({
  businessId,
  autoFetch = true,
}: UseSupplierOptions): UseSuppliersReturn {
  const [suppliers, setSuppliers] = useState<SupplierListItem[]>([]);
  const [allSuppliers, setAllSuppliers] = useState<SupplierListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSuppliers = useCallback(async () => {
    if (!businessId) {
      setSuppliers([]);
      setAllSuppliers([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchSuppliers(businessId);
      setSuppliers(data);
      setAllSuppliers(data);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load suppliers';
      setError(message);
      console.error('[useSuppliers] Error:', err);
    } finally {
      setIsLoading(false);
    }
  }, [businessId]);

  const search = useCallback(
    async (term: string) => {
      if (!businessId) return;

      if (!term.trim()) {
        setSuppliers(allSuppliers);
        return;
      }

      setIsSearching(true);
      setError(null);

      try {
        const data = await searchSuppliers(businessId, term);
        setSuppliers(data);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to search suppliers';
        setError(message);
        console.error('[useSuppliers] Search error:', err);
      } finally {
        setIsSearching(false);
      }
    },
    [businessId, allSuppliers]
  );

  const clearSearch = useCallback(() => {
    setSuppliers(allSuppliers);
  }, [allSuppliers]);

  useEffect(() => {
    if (autoFetch && businessId) {
      loadSuppliers();
    }
  }, [autoFetch, businessId, loadSuppliers]);

  return {
    suppliers,
    isLoading,
    error,
    refetch: loadSuppliers,
    search,
    clearSearch,
    isSearching,
  };
}
